"use client";

import { useState, useEffect, useRef } from "react";

// Next.js API routes are same-origin, so there's no BASE_URL to import —
// every fetch below just hits a relative "/api/ai/..." path.
const STORAGE_KEY = "orane_ai_user_id_v1";

// Swap these for your own assets (drop files in /public and import
// them, e.g. `import AiImage from "/public/ai-img.png"` won't work —
// use a plain path string instead: const AI_IMAGE_SRC = "/ai-img.png";).
const AI_IMAGE_SRC = "/assets/images/ai-img.png";
const AI_ICON_SRC = "/assets/images/ai-icon.png";

const getUserId = () => {
  if (typeof window === "undefined") return "";
  let id = localStorage.getItem(STORAGE_KEY);
  if (!id) {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      id = crypto.randomUUID();
    } else {
      id = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }
    localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
};

// ---------- MARKDOWN & RICH TEXT RENDERER ----------
const renderFormattedContent = (text) => {
  if (!text) return text;

  const paragraphs = text.split(/\n\n+/);

  return paragraphs.map((paragraph, pIdx) => {
    const lines = paragraph.split("\n");
    const elements = [];

    lines.forEach((line, lIdx) => {
      let content = line;
      let isSpecial = false;

      if (line.startsWith("###")) {
        content = (
          <h3 key={`${pIdx}-${lIdx}`} style={{ margin: "12px 0 6px 0", fontSize: "14px", fontWeight: "600", color: "#1554e8" }}>
            {line.replace(/^#+\s*/, "")}
          </h3>
        );
        isSpecial = true;
      } else if (line.startsWith("##")) {
        content = (
          <h2 key={`${pIdx}-${lIdx}`} style={{ margin: "16px 0 8px 0", fontSize: "16px", fontWeight: "700", color: "#1554e8" }}>
            {line.replace(/^#+\s*/, "")}
          </h2>
        );
        isSpecial = true;
      } else if (line.startsWith("#")) {
        content = (
          <h1 key={`${pIdx}-${lIdx}`} style={{ margin: "20px 0 10px 0", fontSize: "18px", fontWeight: "700", color: "#0d3aad" }}>
            {line.replace(/^#+\s*/, "")}
          </h1>
        );
        isSpecial = true;
      } else if (line.match(/^(━+|─+|—+|-{3,}|_{3,}|\*{3,})$/)) {
        content = (
          <div
            key={`${pIdx}-${lIdx}`}
            style={{ borderTop: "2px solid #E5E7EB", margin: "12px 0", opacity: 0.5 }}
          />
        );
        isSpecial = true;
      } else if (line.match(/^[\s]*[•\-\*]\s+/)) {
        const bulletText = line.replace(/^[\s]*[•\-\*]\s+/, "");
        content = (
          <div key={`${pIdx}-${lIdx}`} style={{ marginLeft: "16px", marginBottom: "6px", display: "flex", gap: "8px" }}>
            <span style={{ color: "#1554e8", fontWeight: "600" }}>•</span>
            <span>{formatTextWithEmphasis(bulletText)}</span>
          </div>
        );
        isSpecial = true;
      } else if (line.match(/^[\s]*\d+[\.)]\s+/)) {
        const numText = line.replace(/^[\s]*\d+[\.)]\s+/, "");
        const num = line.match(/\d+/)[0];
        content = (
          <div key={`${pIdx}-${lIdx}`} style={{ marginLeft: "16px", marginBottom: "6px", display: "flex", gap: "8px" }}>
            <span style={{ color: "#1554e8", fontWeight: "600", minWidth: "20px" }}>{num}.</span>
            <span>{formatTextWithEmphasis(numText)}</span>
          </div>
        );
        isSpecial = true;
      } else if (line.includes("|")) {
        const cells = line.split("|").filter((cell) => cell.trim());
        content = (
          <div
            key={`${pIdx}-${lIdx}`}
            style={{
              display: "flex",
              gap: "12px",
              padding: "8px",
              backgroundColor: "#FDFCFB",
              borderRadius: "6px",
              marginBottom: "6px",
              fontSize: "12px",
              borderLeft: "3px solid #1554e8",
            }}
          >
            {cells.map((cell, idx) => (
              <span key={idx} style={{ flex: 1 }}>
                {formatTextWithEmphasis(cell.trim())}
              </span>
            ))}
          </div>
        );
        isSpecial = true;
      } else if (line.includes("`")) {
        content = (
          <code
            key={`${pIdx}-${lIdx}`}
            style={{
              backgroundColor: "#EAF1FF",
              padding: "2px 6px",
              borderRadius: "4px",
              fontFamily: "monospace",
              fontSize: "12px",
              color: "#1554e8",
            }}
          >
            {line.replace(/`/g, "")}
          </code>
        );
        isSpecial = true;
      }

      if (!isSpecial) {
        content = formatTextWithEmphasis(line);
        content = <div key={`${pIdx}-${lIdx}`} style={{ marginBottom: "6px" }}>{content}</div>;
      }

      elements.push(content);
    });

    return (
      <div key={pIdx} style={{ marginBottom: "12px" }}>
        {elements}
      </div>
    );
  });
};

const formatTextWithEmphasis = (text) => {
  if (!text) return text;

  let tempText = text;
  tempText = tempText.replace(/\*\*(.*?)\*\*/g, (match, content) => `<strong>${content}</strong>`);
  tempText = tempText.replace(/\*(.*?)\*/g, (match, content) => `<em>${content}</em>`);

  const htmlParts = tempText.split(/(<strong>.*?<\/strong>|<em>.*?<\/em>)/);

  return htmlParts.map((part, idx) => {
    if (part.startsWith("<strong>")) {
      return (
        <strong key={idx} style={{ fontWeight: "600", color: "#1554e8" }}>
          {part.replace(/<\/?strong>/g, "")}
        </strong>
      );
    } else if (part.startsWith("<em>")) {
      return (
        <em key={idx} style={{ fontStyle: "italic", color: "#555" }}>
          {part.replace(/<\/?em>/g, "")}
        </em>
      );
    }
    return <span key={idx}>{part}</span>;
  });
};

// ---------- Styles ----------
const styles = {
  launcher: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    width: "55px",
    height: "55px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #1554e8 0%, #4d7ef5 100%)",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 8px 28px rgba(21, 84, 232, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    zIndex: 9999,
    transition: "transform 0.2s, box-shadow 0.2s",
    fontFamily: "'DM Sans', system-ui, sans-serif",
  },
  launcherPulse: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "50%",
    background: "#1554e8",
    opacity: 0.4,
    animation: "oranePulse 2s infinite",
  },
  popup: {
    position: "fixed",
    bottom: "100px",
    right: "24px",
    width: "420px",
    height: "700px",
    maxHeight: "calc(100vh - 140px)",
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 20px 60px rgba(13, 30, 90, 0.25)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    border: "1px solid #E3EAFB",
    zIndex: 9998,
    fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
    animation: "oraneSlideUp 0.25s ease-out",
  },
  header: {
    padding: "16px 20px",
    background: "linear-gradient(135deg, #1554e8 0%, #0d3aad 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: "12px" },
  avatar: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    background: "#fff",
    color: "#1554e8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "600",
    fontSize: "18px",
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    border: "1px solid rgba(255,255,255,0.3)",
    overflow: "hidden",
  },
  title: {
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: "14px",
    fontWeight: "600",
    color: "#fff",
    lineHeight: 1.1,
  },
  subtitle: {
    fontSize: "11px",
    color: "rgba(255,255,255,0.85)",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "3px",
  },
  statusDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: "#10B981",
    display: "inline-block",
    boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.25)",
  },
  headerActions: { display: "flex", gap: "6px" },
  iconBtn: {
    background: "rgba(255,255,255,0.15)",
    border: "none",
    color: "#fff",
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.15s",
    padding: 0,
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "20px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    background: "#FDFCFB",
  },
  row: { display: "flex", gap: "8px", maxWidth: "90%" },
  rowUser: { alignSelf: "flex-end", flexDirection: "row-reverse" },
  rowAssistant: { alignSelf: "flex-start" },
  bubble: {
    padding: "12px 14px",
    borderRadius: "14px",
    fontSize: "13.5px",
    lineHeight: 1.6,
    wordWrap: "break-word",
    overflow: "hidden",
  },
  bubbleUser: { background: "#1554e8", color: "#fff", borderBottomRightRadius: "4px" },
  bubbleAssistant: { background: "#fff", color: "#111", border: "1px solid #E3EAFB", borderBottomLeftRadius: "4px" },
  typing: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "12px 16px",
    background: "#fff",
    border: "1px solid #E3EAFB",
    borderRadius: "14px",
    borderBottomLeftRadius: "4px",
    width: "fit-content",
  },
  dot: { width: "7px", height: "7px", borderRadius: "50%", background: "#A8C5FA", animation: "oraneBounce 1.3s infinite ease-in-out" },
  inputArea: {
    padding: "12px 16px",
    borderTop: "1px solid #E3EAFB",
    background: "#fff",
    display: "flex",
    gap: "8px",
    alignItems: "flex-end",
    flexShrink: 0,
  },
  textarea: {
    flex: 1,
    resize: "none",
    border: "1px solid #E3EAFB",
    borderRadius: "10px",
    padding: "10px 12px",
    fontSize: "14px",
    fontFamily: "inherit",
    outline: "none",
    color: "#111",
    maxHeight: "100px",
    minHeight: "40px",
    lineHeight: 1.4,
    transition: "border-color 0.15s",
  },
  sendBtn: {
    background: "#1554e8",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    width: "40px",
    height: "40px",
    fontSize: "16px",
    cursor: "pointer",
    fontFamily: "inherit",
    boxShadow: "0 3px 10px rgba(21, 84, 232, 0.28)",
    transition: "all 0.15s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  sendBtnDisabled: { opacity: 0.4, cursor: "not-allowed", boxShadow: "none" },
  footer: { padding: "8px 16px", fontSize: "10.5px", color: "#9C9C9C", textAlign: "center", background: "#FDFCFB" },
  footerBrand: { fontWeight: "600", color: "#1554e8", letterSpacing: "0.5px" },
  quickPromptsContainer: {
    padding: "12px 10px",
    borderTop: "1px solid #E3EAFB",
    background: "#fff",
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    flexShrink: 0,
  },
  quickPromptBtn: {
    padding: "6px 12px",
    fontSize: "12px",
    border: "1px solid #E3EAFB",
    borderRadius: "8px",
    background: "#FDFCFB",
    color: "#1554e8",
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: "500",
    transition: "all 0.15s",
  },
};

export default function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const [chat, setChat] = useState([
    {
      role: "assistant",
      type: "card",
      name: "Aria ✨",
      subtitle: "Orane Consulting Assistant",
      status: "Online",
      content: `Hi, I'm Aria 👋

I can help you explore Orane's enterprise transformation, SAP & data, and AI solutions.

**Want to know how Orane can help your business? Ask Aria.**`,
    },
  ]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [userId, setUserId] = useState("");
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);

  const WELCOME = chat[0];

  const quickPrompts = ["Enterprise Transformation", "Data & SAP BDC", "AI & Intelligence", "Industries We Serve"];

  // Resolve the visitor id client-side only (localStorage isn't
  // available during SSR).
  useEffect(() => {
    setUserId(getUserId());
  }, []);

  // Load chat history once we have a userId
  useEffect(() => {
    if (!userId) return;

    const loadHistory = async () => {
      try {
        setInitializing(true);
        const response = await fetch(`/api/ai/chat-history/${userId}?limit=50`);
        // A 404/500 returns an HTML page — skip it instead of throwing on .json()
        const isJson = response.headers.get("content-type")?.includes("application/json");
        if (!response.ok || !isJson) return;
        const data = await response.json();

        if (data.success && data.history && data.history.length > 0) {
          setChat([WELCOME, ...data.history]);
        }
      } catch (error) {
        console.error("Failed to load chat history:", error);
      } finally {
        setInitializing(false);
      }
    };

    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container && isOpen) {
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 100);
    }
  }, [chat, loading, isOpen]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "40px";
      ta.style.height = Math.min(ta.scrollHeight, 100) + "px";
    }
  }, [message]);

  const handleStreamingResponse = async (trimmed) => {
    setChat((prev) => [...prev, { role: "user", content: trimmed }]);
    setMessage("");
    setLoading(true);

    abortControllerRef.current = new AbortController();
    let fullResponse = "";
    let ragSources = [];

    try {
      const response = await fetch(`/api/ai/ask/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, message: trimmed }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      setChat((prev) => [...prev, { role: "assistant", content: "", sources: [] }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          setLoading(false);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines[lines.length - 1];

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i];
          if (!line.startsWith("data: ")) continue;

          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === "chunk") {
              fullResponse += data.content;
              setChat((prev) => {
                const updated = [...prev];
                if (updated[updated.length - 1].role === "assistant") {
                  updated[updated.length - 1].content = fullResponse;
                }
                return updated;
              });
            } else if (data.type === "metadata") {
              ragSources = data.rag_sources || [];
            } else if (data.type === "done") {
              fullResponse = data.full_reply;
              setChat((prev) => {
                const updated = [...prev];
                if (updated[updated.length - 1].role === "assistant") {
                  updated[updated.length - 1].content = data.full_reply;
                  updated[updated.length - 1].sources = ragSources;
                }
                return updated;
              });
              setLoading(false);
            } else if (data.type === "error") {
              throw new Error(data.message);
            }
          } catch (parseError) {
            console.error("Parse error:", parseError);
          }
        }
      }
    } catch (error) {
      console.error("Streaming error:", error);
      if (error.name !== "AbortError") {
        setChat((prev) => [
          ...prev,
          { role: "assistant", content: "😔 Sorry, I couldn't reach the server. Please try again." },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || loading || !userId) return;
    await handleStreamingResponse(trimmed);
  };

  const handleQuickPrompt = (prompt) => {
    setMessage(prompt);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <style>{`
        @keyframes oraneBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
        @keyframes oraneSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes oranePulse {
          0% { transform: scale(1); opacity: 0.4; }
          70% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        .orane-launcher:hover { transform: scale(1.08); box-shadow: 0 12px 36px rgba(21, 84, 232, 0.5) !important; }
        .orane-iconbtn:hover { background: rgba(255,255,255,0.28) !important; }
        .orane-sendbtn:hover:not(:disabled) { background: #0d3aad !important; transform: translateY(-1px); }
        .orane-textarea:focus { border-color: #1554e8 !important; }
        .orane-msgs::-webkit-scrollbar { width: 5px; }
        .orane-msgs::-webkit-scrollbar-thumb { background: #E3EAFB; border-radius: 3px; }
        .orane-msgs::-webkit-scrollbar-thumb:hover { background: #A8C5FA; }
        .orane-prompt:hover { background: #E3EBFF !important; border-color: #A8C5FA !important; }

        .message-content h1 { color: #0d3aad; font-size: 18px; margin: 12px 0; }
        .message-content h2 { color: #1554e8; font-size: 16px; margin: 10px 0; }
        .message-content h3 { color: #1554e8; font-size: 14px; margin: 8px 0; }
        .message-content strong { font-weight: 600; color: #1554e8; }
        .message-content em { font-style: italic; }
        .message-content ul, .message-content ol { margin-left: 16px; }
        .message-content li { margin: 4px 0; }
        .message-content code { background: #EAF1FF; padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 12px; }
        .message-content blockquote { border-left: 3px solid #1554e8; padding-left: 12px; margin-left: 0; opacity: 0.8; }

        @media (max-width: 480px) {
          .orane-popup { width: calc(100vw - 24px) !important; height: calc(100vh - 120px) !important; right: 12px !important; bottom: 90px !important; }
          .orane-launcher { bottom: 16px !important; right: 16px !important; }
        }
      `}</style>

      {isOpen && (
        <div style={styles.popup} className="orane-popup">
          <div style={styles.header}>
            <div style={styles.headerLeft}>
              <div style={styles.avatar} className="avatar-img">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={AI_IMAGE_SRC}
                  alt="Aria"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
              <div>
                <h3 className="font-semibold text-lg" style={{ margin: 0 }}>Aria</h3>
                <div style={styles.title}>Orane Consulting — Enterprise AI</div>
                <div style={styles.subtitle}>
                  <span style={styles.statusDot}></span>
                  {loading ? "Streaming..." : "Online · Ask about our services"}
                </div>
              </div>
            </div>
            <div style={styles.headerActions}>
              <button style={styles.iconBtn} className="orane-iconbtn" onClick={() => setIsOpen(false)} title="Minimize">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>
          </div>

          <div style={styles.messages} className="orane-msgs" ref={messagesContainerRef}>
            {initializing ? (
              <div style={{ color: "#9C9C9C", fontSize: "13px", textAlign: "center", marginTop: "20px" }}>
                📂 Loading your chat history...
              </div>
            ) : (
              chat.map((item, index) => {
                const isUser = item.role === "user";
                return (
                  <div key={index}>
                    <div style={{ ...styles.row, ...(isUser ? styles.rowUser : styles.rowAssistant) }}>
                      <div
                        style={{ ...styles.bubble, ...(isUser ? styles.bubbleUser : styles.bubbleAssistant) }}
                        className="message-content"
                      >
                        {isUser ? item.content : renderFormattedContent(item.content)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {loading && !chat[chat.length - 1]?.content && (
              <div style={{ ...styles.row, ...styles.rowAssistant }}>
                <div style={styles.typing}>
                  <span style={{ ...styles.dot, animationDelay: "0s" }}></span>
                  <span style={{ ...styles.dot, animationDelay: "0.2s" }}></span>
                  <span style={{ ...styles.dot, animationDelay: "0.4s" }}></span>
                </div>
              </div>
            )}
          </div>

          {!message.trim() && !initializing && (
            <div style={styles.quickPromptsContainer}>
              {quickPrompts.map((prompt, idx) => (
                <button
                  key={idx}
                  className="orane-prompt"
                  onClick={() => handleQuickPrompt(prompt)}
                  style={styles.quickPromptBtn}
                  title={prompt}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}

          <div style={styles.inputArea}>
            <textarea
              ref={textareaRef}
              style={styles.textarea}
              className="orane-textarea"
              placeholder="Ask about Orane's services..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            <button
              style={{ ...styles.sendBtn, ...(loading || !message.trim() ? styles.sendBtnDisabled : {}) }}
              className="orane-sendbtn"
              onClick={handleSend}
              disabled={loading || !message.trim()}
              title="Send"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>

          <div style={styles.footer}>
            Powered by <span style={styles.footerBrand}>Orane Consulting</span> · AI Assistant
          </div>
        </div>
      )}

      {showTooltip && !isOpen && (
        <div
          onClick={() => {
            setIsOpen(true);
            setShowTooltip(false);
          }}
          className="ai-tooltip"
          style={{
            position: "fixed",
            bottom: "22px",
            right: "90px",
            background: "#333",
            color: "#fff",
            padding: "12px 18px",
            borderRadius: "14px",
            fontSize: "16px",
            fontWeight: "600",
            zIndex: 999998,
            cursor: "pointer",
            boxShadow: "0 10px 30px rgba(21,84,232,.35)",
            animation: "tooltipBounce 2s infinite",
          }}
        >
          <small style={{ opacity: 0.9 }}>Hi! I'm Aria, Orane's AI Assistant.</small>
        </div>
      )}

      <button
        style={styles.launcher}
        className="orane-launcher"
        data-ecap-chat="true"
        onClick={() => {
          setIsOpen(!isOpen);
          setShowTooltip(false);
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {!isOpen && <div style={styles.launcherPulse}></div>}
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={AI_ICON_SRC}
            className="ai-launcher-icon"
            alt="Aria Chat"
            style={{ width: "26px", height: "26px", objectFit: "contain" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
      </button>
    </>
  );
}
